/**
 * Security Settings Page Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Security settings including password policy, session settings, and lockout rules.
 */

import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, RotateCcw, Shield, AlertTriangle } from 'lucide-react';
import { settingsService } from '../../services/admin';
import type { SecuritySettings, SecuritySettingsUpdateData } from '../../types/admin';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/apiErrorHandler';

const SecuritySettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<SecuritySettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [formData, setFormData] = useState<SecuritySettingsUpdateData>({});

  // Load settings
  useEffect(() => {
    setIsLoading(true);
    settingsService
      .getSecuritySettings()
      .then((data) => {
        setSettings(data);
        setFormData({
          min_password_length: data.min_password_length,
          require_uppercase: data.require_uppercase,
          require_lowercase: data.require_lowercase,
          require_numbers: data.require_numbers,
          require_special_chars: data.require_special_chars,
          password_expiry_days: data.password_expiry_days,
          password_history_count: data.password_history_count,
          session_timeout_minutes: data.session_timeout_minutes,
          max_concurrent_sessions: data.max_concurrent_sessions,
          max_failed_attempts: data.max_failed_attempts,
          lockout_duration_minutes: data.lockout_duration_minutes,
          require_2fa_for_admin: data.require_2fa_for_admin,
          require_2fa_for_all: data.require_2fa_for_all,
        });
      })
      .catch((err) => {
        console.error('Failed to load settings:', err);
        toast.error('Failed to load settings');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Track changes
  useEffect(() => {
    if (!settings) return;

    const changed =
      formData.min_password_length !== settings.min_password_length ||
      formData.require_uppercase !== settings.require_uppercase ||
      formData.require_lowercase !== settings.require_lowercase ||
      formData.require_numbers !== settings.require_numbers ||
      formData.require_special_chars !== settings.require_special_chars ||
      formData.password_expiry_days !== settings.password_expiry_days ||
      formData.password_history_count !== settings.password_history_count ||
      formData.session_timeout_minutes !== settings.session_timeout_minutes ||
      formData.max_concurrent_sessions !== settings.max_concurrent_sessions ||
      formData.max_failed_attempts !== settings.max_failed_attempts ||
      formData.lockout_duration_minutes !== settings.lockout_duration_minutes ||
      formData.require_2fa_for_admin !== settings.require_2fa_for_admin ||
      formData.require_2fa_for_all !== settings.require_2fa_for_all;

    setHasChanges(changed);
  }, [formData, settings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await settingsService.updateSecuritySettings(formData);
      setSettings(updated);
      setHasChanges(false);
      toast.success('Security settings saved successfully');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save settings'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    setIsResetting(true);
    try {
      const defaults = await settingsService.resetSecuritySettingsToDefaults();
      setSettings(defaults);
      setFormData({
        min_password_length: defaults.min_password_length,
        require_uppercase: defaults.require_uppercase,
        require_lowercase: defaults.require_lowercase,
        require_numbers: defaults.require_numbers,
        require_special_chars: defaults.require_special_chars,
        password_expiry_days: defaults.password_expiry_days,
        password_history_count: defaults.password_history_count,
        session_timeout_minutes: defaults.session_timeout_minutes,
        max_concurrent_sessions: defaults.max_concurrent_sessions,
        max_failed_attempts: defaults.max_failed_attempts,
        lockout_duration_minutes: defaults.lockout_duration_minutes,
        require_2fa_for_admin: defaults.require_2fa_for_admin,
        require_2fa_for_all: defaults.require_2fa_for_all,
      });
      setHasChanges(false);
      toast.success('Settings reset to Swiss banking defaults');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to reset settings'));
    } finally {
      setIsResetting(false);
    }
  };

  const handleDiscard = () => {
    if (!settings) return;
    setFormData({
      min_password_length: settings.min_password_length,
      require_uppercase: settings.require_uppercase,
      require_lowercase: settings.require_lowercase,
      require_numbers: settings.require_numbers,
      require_special_chars: settings.require_special_chars,
      password_expiry_days: settings.password_expiry_days,
      password_history_count: settings.password_history_count,
      session_timeout_minutes: settings.session_timeout_minutes,
      max_concurrent_sessions: settings.max_concurrent_sessions,
      max_failed_attempts: settings.max_failed_attempts,
      lockout_duration_minutes: settings.lockout_duration_minutes,
      require_2fa_for_admin: settings.require_2fa_for_admin,
      require_2fa_for_all: settings.require_2fa_for_all,
    });
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#002B45] rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security Settings</h1>
          <p className="text-gray-500 mt-1">
            Configure password policies, session rules, and security features
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleDiscard} disabled={!hasChanges || isSaving}>
            <RefreshCw size={16} className="mr-2" />
            Discard
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
            <Save size={16} className="mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Swiss banking defaults banner */}
      <div className="bg-[#002B45] text-white rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={24} className="text-[#F7B500]" />
          <div>
            <p className="font-medium">Swiss Banking Security Standards</p>
            <p className="text-sm text-white/70">
              Reset to FINMA-compliant security defaults
            </p>
          </div>
        </div>
        {/* TASK-607: outline variant carries bg-white — without bg-transparent
            the label was white-on-white (the audit's blank FINMA pill). */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleResetToDefaults}
          disabled={isResetting}
          className="bg-transparent border-white/30 text-white hover:bg-white/10"
        >
          <RotateCcw size={16} className="mr-2" />
          {isResetting ? 'Resetting...' : 'Reset to Defaults'}
        </Button>
      </div>

      {/* Password Policy */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Password Policy</h2>

        <div className="space-y-4">
          {/* Min length */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Password Length
            </label>
            <input
              type="number"
              min={8}
              max={32}
              value={formData.min_password_length || 12}
              onChange={(e) =>
                setFormData({ ...formData, min_password_length: parseInt(e.target.value) || 12 })
              }
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
          </div>

          {/* Character requirements */}
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.require_uppercase ?? true}
                onChange={(e) => setFormData({ ...formData, require_uppercase: e.target.checked })}
                className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
              />
              <span className="text-sm text-gray-700">Require uppercase letter</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.require_lowercase ?? true}
                onChange={(e) => setFormData({ ...formData, require_lowercase: e.target.checked })}
                className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
              />
              <span className="text-sm text-gray-700">Require lowercase letter</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.require_numbers ?? true}
                onChange={(e) => setFormData({ ...formData, require_numbers: e.target.checked })}
                className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
              />
              <span className="text-sm text-gray-700">Require number</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.require_special_chars ?? true}
                onChange={(e) =>
                  setFormData({ ...formData, require_special_chars: e.target.checked })
                }
                className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
              />
              <span className="text-sm text-gray-700">Require special character</span>
            </label>
          </div>

          {/* Password expiry */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password Expiry (days)
              </label>
              <input
                type="number"
                min={0}
                max={365}
                value={formData.password_expiry_days ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    password_expiry_days: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
                placeholder="No expiry"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password History Count
              </label>
              <input
                type="number"
                min={0}
                max={24}
                value={formData.password_history_count ?? 5}
                onChange={(e) =>
                  setFormData({ ...formData, password_history_count: parseInt(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              />
              <p className="text-xs text-gray-500 mt-1">Prevent reuse of recent passwords</p>
            </div>
          </div>
        </div>
      </div>

      {/* Session Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Settings</h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Session Timeout (minutes)
            </label>
            <input
              type="number"
              min={5}
              max={1440}
              value={formData.session_timeout_minutes ?? 480}
              onChange={(e) =>
                setFormData({ ...formData, session_timeout_minutes: parseInt(e.target.value) || 480 })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
            <p className="text-xs text-gray-500 mt-1">
              {settingsService.formatSessionTimeout(formData.session_timeout_minutes ?? 480)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Concurrent Sessions
            </label>
            <input
              type="number"
              min={1}
              max={10}
              value={formData.max_concurrent_sessions ?? ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  max_concurrent_sessions: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              placeholder="Unlimited"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited</p>
          </div>
        </div>
      </div>

      {/* Lockout Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Lockout</h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Failed Login Attempts
            </label>
            <input
              type="number"
              min={3}
              max={20}
              value={formData.max_failed_attempts ?? 5}
              onChange={(e) =>
                setFormData({ ...formData, max_failed_attempts: parseInt(e.target.value) || 5 })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lockout Duration (minutes)
            </label>
            <input
              type="number"
              min={5}
              max={1440}
              value={formData.lockout_duration_minutes ?? 30}
              onChange={(e) =>
                setFormData({ ...formData, lockout_duration_minutes: parseInt(e.target.value) || 30 })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Two-Factor Authentication</h2>

        <div className="space-y-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.require_2fa_for_admin ?? false}
              onChange={(e) => setFormData({ ...formData, require_2fa_for_admin: e.target.checked })}
              className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
            />
            <div>
              <span className="font-medium text-gray-900">Require 2FA for Admins</span>
              <p className="text-sm text-gray-500">Administrators must use two-factor authentication</p>
            </div>
          </label>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.require_2fa_for_all ?? false}
              onChange={(e) => setFormData({ ...formData, require_2fa_for_all: e.target.checked })}
              className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
            />
            <div>
              <span className="font-medium text-gray-900">Require 2FA for All Users</span>
              <p className="text-sm text-gray-500">All users must use two-factor authentication</p>
            </div>
          </label>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          Changes to security settings will apply to all users. Users with active sessions may be
          affected immediately. Consider notifying your team before making significant changes.
        </p>
      </div>
    </div>
  );
};

export default SecuritySettingsPage;
